import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGalleryItems } from '../hooks/useSupabase';
import ImageWithFallback from './ImageWithFallback';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight } = FiIcons;

const AcademyImagesCarousel = () => {
  // Fetch images tagged with 'academy carousel'
  const { data: items, loading } = useGalleryItems('academy carousel');

  // Filter only items that are images and have a URL
  const images = (items || []).filter(
    (item) => (!item.type || item.type === 'image') && item.url
  );

  const [current, setCurrent] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const goNext = useCallback(() => {
    if (!images.length) return;
    setCurrent((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    if (!images.length) return;
    setCurrent((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Auto-play (pause on hover)
  useEffect(() => {
    if (!images.length || isHovered) return;
    const interval = setInterval(goNext, 4000);
    return () => clearInterval(interval);
  }, [images.length, isHovered, goNext]);

  // Keyboard navigation (left/right arrows)
  useEffect(() => {
    const handleKey = (e) => {
      if (!images.length) return;
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [images.length, goNext, goPrev]);

  // If no images found or loading, show fallback static image
  if (loading || !images.length) {
    return (
      <div className="mb-8">
        <ImageWithFallback
          src="https://images.unsplash.com/photo-1579952363873-27d3bfad9c0d?ixlib=rb-4.0.3&w=600&h=400&auto=format&fit=crop&crop=center"
          alt="Κορίτσια που παίζουν ποδόσφαιρο"
          className="w-full max-w-md mx-auto rounded-2xl shadow-2xl border-4 border-white/20"
        />
      </div>
    );
  }

  const active = images[current];
  const activeKey = active.id || active.url || current;

  return (
    <div className="mb-8 relative w-full max-w-md mx-auto">
      {/* Glow frame */}
      <div
        className="relative rounded-3xl p-[3px] bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 shadow-[0_20px_45px_rgba(15,23,42,0.45)]"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative aspect-[3/2] rounded-3xl overflow-hidden bg-gray-200">
          {/* Inner darkish background to make colors pop */}
          <div className="absolute inset-0 bg-slate-900/40 mix-blend-multiply pointer-events-none" />

          {/* Animated slide */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeKey}
              initial={{ opacity: 0, scale: 1.03, x: 40 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.97, x: -40 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              className="w-full h-full"
            >
              <ImageWithFallback
                src={active.url}
                alt={active.title || 'Ακαδημία ποδοσφαίρου'}
                className="w-full h-full object-cover"
              />
            </motion.div>
          </AnimatePresence>

          {/* Top-left tag */}
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center rounded-full bg-black/60 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/90 backdrop-blur-sm">
              Academy Moments
            </span>
          </div>

          {/* Gradient + title overlay */}
          {(active.title || active.description) && (
            <div className="absolute inset-x-0 bottom-0">
              <div className="bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 pt-8 pb-4">
                {active.title && (
                  <p className="text-white text-sm md:text-base font-semibold text-center">
                    {active.title}
                  </p>
                )}
                {active.description && (
                  <p className="text-white/80 text-xs md:text-sm text-center mt-1 line-clamp-2">
                    {active.description}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Left Arrow */}
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/40 hover:bg-black/70 border border-white/20 flex items-center justify-center backdrop-blur-sm transition-all duration-200 hover:scale-110"
            aria-label="Previous image"
          >
            <SafeIcon
              icon={FiChevronLeft}
              className="w-5 h-5 text-white drop-shadow"
            />
          </button>

          {/* Right Arrow */}
          <button
            type="button"
            onClick={goNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-black/40 hover:bg-black/70 border border-white/20 flex items-center justify-center backdrop-blur-sm transition-all duration-200 hover:scale-110"
            aria-label="Next image"
          >
            <SafeIcon
              icon={FiChevronRight}
              className="w-5 h-5 text-white drop-shadow"
            />
          </button>

          {/* Subtle animated glow bottom center */}
          <div className="pointer-events-none absolute -bottom-10 left-1/2 h-16 w-40 -translate-x-1/2 rounded-full bg-primary-500/40 blur-2xl opacity-70" />
        </div>
      </div>

      {/* Dots indicator */}
      <div className="flex justify-center gap-2 mt-4">
        {images.map((img, idx) => (
          <button
            key={img.id || idx}
            onClick={() => setCurrent(idx)}
            className={`relative flex items-center justify-center transition-transform duration-300 ${
              idx === current ? 'scale-110' : 'scale-90 hover:scale-100'
            }`}
            aria-label={`Go to slide ${idx + 1}`}
          >
            {/* outer ring */}
            <span
              className={`w-3 h-3 rounded-full border transition-colors duration-300 ${
                idx === current
                  ? 'border-white'
                  : 'border-white/40'
              }`}
            />
            {/* inner dot */}
            <span
              className={`absolute w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                idx === current
                  ? 'bg-white'
                  : 'bg-white/50'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default AcademyImagesCarousel;